import { defineStore } from "pinia";
import { ref, computed, readonly } from "vue";
import { authApi } from "@/api/auth";
import type { User, LoginForm, RegisterForm, AuthTokens } from "@/types/user";

export const useUserStore = defineStore("user", () => {
  // 状态
  const user = ref<User | null>(null);
  const tokens = ref<AuthTokens | null>(null);
  const loading = ref(false);

  // 计算属性
  const isAuthenticated = computed(() => {
    return !!tokens.value?.accessToken && !!user.value;
  });

  const memberLevel = computed(() => {
    return user.value?.memberLevel || null;
  });

  // Actions
  const login = async (credentials: LoginForm) => {
    loading.value = true;
    try {
      const response = await authApi.login(credentials);

      tokens.value = {
        accessToken: response.accessToken,
        refreshToken: response.refreshToken,
        tokenType: response.tokenType,
        expiresIn: response.expiresIn,
      };

      user.value = response.user;

      // 保存到本地存储
      localStorage.setItem("mier_tokens", JSON.stringify(tokens.value));
      localStorage.setItem("mier_user", JSON.stringify(user.value));

      return response;
    } catch (error) {
      throw error;
    } finally {
      loading.value = false;
    }
  };

  const register = async (userData: RegisterForm) => {
    loading.value = true;
    try {
      const response = await authApi.register(userData);

      tokens.value = {
        accessToken: response.accessToken,
        refreshToken: response.refreshToken,
        tokenType: response.tokenType,
        expiresIn: response.expiresIn,
      };

      user.value = response.user;

      // 保存到本地存储
      localStorage.setItem("mier_tokens", JSON.stringify(tokens.value));
      localStorage.setItem("mier_user", JSON.stringify(user.value));

      return response;
    } catch (error) {
      throw error;
    } finally {
      loading.value = false;
    }
  };

  const logout = async () => {
    try {
      if (tokens.value) {
        await authApi.logout();
      }
    } catch (error) {
    } finally {
      // 清除状态和本地存储
      user.value = null;
      tokens.value = null;
      localStorage.removeItem("mier_tokens");
      localStorage.removeItem("mier_user");
    }
  };

  const refreshTokens = async () => {
    if (!tokens.value?.refreshToken) {
      throw new Error("No refresh token available");
    }

    try {
      const response = await authApi.refresh(tokens.value.refreshToken);

      tokens.value = {
        accessToken: response.accessToken,
        refreshToken: response.refreshToken,
        tokenType: response.tokenType,
        expiresIn: response.expiresIn,
      };

      user.value = response.user;

      // 更新本地存储
      localStorage.setItem("mier_tokens", JSON.stringify(tokens.value));
      localStorage.setItem("mier_user", JSON.stringify(user.value));

      return response;
    } catch (error) {
      // 刷新失败，清除认证状态
      await logout();
      throw error;
    }
  };

  const restoreAuth = async () => {
    const savedTokens = localStorage.getItem("mier_tokens");
    const savedUser = localStorage.getItem("mier_user");

    if (savedTokens && savedUser) {
      try {
        tokens.value = JSON.parse(savedTokens);
        user.value = JSON.parse(savedUser);

        // 如果没有accessToken，不需要验证
        if (!tokens.value?.accessToken) {
          return;
        }

        // 尝试验证令牌有效性
        try {
          await authApi.getProfile();
        } catch (error) {}
      } catch (error) {}
    }
  };

  const updateProfile = (newUserData: Partial<User>) => {
    if (user.value) {
      user.value = { ...user.value, ...newUserData };
      localStorage.setItem("mier_user", JSON.stringify(user.value));
    }
  };

  return {
    // 状态
    user: readonly(user),
    tokens: readonly(tokens),
    loading: readonly(loading),

    // 计算属性
    isAuthenticated,
    memberLevel,

    // Actions
    login,
    register,
    logout,
    refreshTokens,
    restoreAuth,
    updateProfile,
  };
});
